import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Alert,
  Tooltip,
  IconButton,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { TimeOffType, HrCategory, HalfDayPeriod, TimeOffFormData, User } from '../types';

interface TimeOffRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: TimeOffFormData) => void;
  initialDates?: string[];
  userId: number;
  userName: string;
  isAdmin?: boolean;
  users?: User[];
  publicHolidays?: any[];
  userOffice?: string;
}

const TimeOffRequestModal: React.FC<TimeOffRequestModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialDates = [],
  userId,
  userName,
  isAdmin = false,
  users = [],
  publicHolidays = [],
  userOffice,
}) => {
  const [formData, setFormData] = useState<TimeOffFormData>({
    dates: initialDates || [],
    holidayType: 'full_day',
    hrCategory: 'paid_leave',
    notes: '',
  });

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllDates, setShowAllDates] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        dates: initialDates || [],
        holidayType: 'full_day',
        hrCategory: 'paid_leave',
        notes: '',
      });
      setSelectedUser(null);
      setErrors([]);
      setShowAllDates(false);
    }
  }, [open, initialDates]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      date: event.target.value,
    }));
    // Clear errors when user makes changes
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleHolidayTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const holidayType = event.target.value as TimeOffType;
    setFormData(prev => ({
      ...prev,
      holidayType,
      halfDayPeriod: holidayType === 'half_day' ? prev.halfDayPeriod : undefined,
      hrCategory: holidayType === 'remote' ? 'other' : prev.hrCategory,
    }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleHrCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const hrCategory = event.target.value as HrCategory;
    setFormData(prev => ({
      ...prev,
      hrCategory,
    }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleHalfDayPeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      halfDayPeriod: event.target.value as HalfDayPeriod,
    }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      notes: event.target.value,
    }));
  };

  const validateForm = (): string[] => {
    const newErrors: string[] = [];

    if (!formData.dates || formData.dates.length === 0) {
      newErrors.push('Please select at least one date');
    }

    if (!formData.holidayType) {
      newErrors.push('Please select a holiday type');
    }

    if (!formData.hrCategory && formData.holidayType !== 'remote') {
      newErrors.push('Please select an HR category');
    }

    if (formData.holidayType === 'half_day' && !formData.halfDayPeriod) {
      newErrors.push('Please select morning or afternoon for half day requests');
    }

    // Validate each date is not more than 15 weeks in the past and not a weekend
    if (formData.dates && formData.dates.length > 0) {
      for (const dateStr of formData.dates) {
        const selectedDate = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const fifteenWeeksAgo = new Date(today);
        fifteenWeeksAgo.setDate(today.getDate() - 7 * 15);

        if (selectedDate < fifteenWeeksAgo) {
          newErrors.push(`Cannot select dates more than 15 weeks in the past: ${dateStr}`);
        }

        // Check if it's a weekend
        const dayOfWeek = selectedDate.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          newErrors.push(`Cannot request time off on weekends: ${dateStr}`);
        }

        // Check if it's a bank holiday
        const isHoliday = publicHolidays.some(h => {
          if (h.date !== dateStr) return false;
          
          // If user has no office, block all holidays
          if (!userOffice) return true;

          // Check if holiday is specific to user's office
          const holidayOffice = (h as any).office;
          
          if (holidayOffice) {
            return holidayOffice === userOffice || holidayOffice === 'ALL';
          }

          // Fallback to name checking if office property is missing
          if (userOffice === 'UK' && h.name.includes('(UK)')) return true;
          if (userOffice === 'FR' && h.name.includes('(FR)')) return true;
          
          return false;
        });

        if (isHoliday) {
          newErrors.push(`Cannot request time off on bank holidays: ${dateStr}`);
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const submissionData = {
        ...formData,
        userId: selectedUser ? selectedUser.id : undefined
      };
      onSubmit(submissionData);
      onClose();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'An error occurred while submitting the request']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getHolidayTypeDescription = (type: TimeOffType): string => {
    switch (type) {
      case 'full_day':
        return 'Request the entire day as vacation time';
      case 'half_day':
        return 'Request either morning or afternoon off';
      case 'remote':
        return 'Work remotely from home or another location';
      default:
        return '';
    }
  };

  const getHrCategoryDescription = (category: HrCategory): string => {
    switch (category) {
      case 'paid_leave':
        return 'Paid vacation or holiday time';
      case 'rr':
        return 'Rest & Recuperation day for wellness and recovery';
      case 'sick_leave':
        return 'Medical leave or illness';
      case 'unpaid_leave':
        return 'Unpaid leave or absence';
      case 'part_time':
        return 'Part-time work schedule';
      case 'other':
        return 'Other HR category';
      default:
        return '';
    }
  };

  const getMinDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="h2">
            Request Time Off
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form noValidate onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}

          {/* User Selection (Admin Only) */}
          {isAdmin && (
            <Box sx={{ mb: 3 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.name}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Create request for user (Optional)"
                    placeholder="Select a user..."
                    helperText="Leave empty to create request for yourself"
                  />
                )}
              />
            </Box>
          )}

          {/* Selected Dates Display */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              color={errors.some((e) => e.toLowerCase().includes('date')) ? 'error' : 'inherit'}
            >
              Selected Dates ({formData.dates.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(showAllDates ? formData.dates : formData.dates.slice(0, 10)).map((date, index) => (
                <Chip
                  key={date}
                  label={new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
              {formData.dates.length > 10 && (
                <Chip
                  label={showAllDates ? "Show Less" : `+${formData.dates.length - 10} more`}
                  size="small"
                  onClick={() => setShowAllDates(!showAllDates)}
                  color="default"
                  variant="outlined"
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You are requesting time off for {formData.dates.length} day{formData.dates.length > 1 ? 's' : ''}.
              All selected dates will have the same time off type and notes.
            </Typography>
          </Box>

          {/* Holiday Type Selection */}
          <FormControl
            component="fieldset"
            sx={{ mb: 3 }}
            error={errors.some((e) => e.toLowerCase().includes('holiday type'))}
          >
            <FormLabel component="legend">
              Holiday Type
              <Tooltip title="Select the type of holiday you want to request">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </FormLabel>
            <RadioGroup
              value={formData.holidayType}
              onChange={handleHolidayTypeChange}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="full_day"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Full Day Off</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getHolidayTypeDescription('full_day')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="half_day"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Half Day</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Request either morning or afternoon off
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="remote"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Remote Working</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getHolidayTypeDescription('remote')}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* HR Category Selection */}
          {formData.holidayType !== 'remote' && (
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">
                HR Category
                <Tooltip title="Select the HR category for this request">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </FormLabel>
              <RadioGroup
                value={formData.hrCategory}
                onChange={handleHrCategoryChange}
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="paid_leave"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Paid Leave</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getHrCategoryDescription('paid_leave')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="rr"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">R&R Day</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getHrCategoryDescription('rr')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="sick_leave"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Sick Leave</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getHrCategoryDescription('sick_leave')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="unpaid_leave"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Unpaid Leave</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getHrCategoryDescription('unpaid_leave')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="part_time"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Part-Time</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getHrCategoryDescription('part_time')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Other</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getHrCategoryDescription('other')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* Half Day Period Selection (only show for half day type) */}
          {formData.holidayType === 'half_day' && (
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Half Day Period</FormLabel>
              <RadioGroup
                value={formData.halfDayPeriod || ''}
                onChange={handleHalfDayPeriodChange}
                row
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="morning"
                  control={<Radio />}
                  label="Morning"
                />
                <FormControlLabel
                  value="afternoon"
                  control={<Radio />}
                  label="Afternoon"
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* Notes */}
          <TextField
            label="Notes (Optional)"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleNotesChange}
            fullWidth
            placeholder="Add any additional notes or context for your request..."
            sx={{ mb: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TimeOffRequestModal;